import { useState, useCallback } from 'react';
import { EmailMessageSummary, EmailMessage } from '../types';
import { aiApi } from '../services/communicationsApi';

export const useAiMessages = () => {
  const [messages, setMessages] = useState<EmailMessageSummary[]>([]);
  const [selectedMessage, setSelectedMessage] = useState<EmailMessage | null>(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [messageLoading, setMessageLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchMessages = useCallback(async (channelId: number, folder: string) => {
    setLoading(true);
    setError(null);
    setMessages([]);
    setSelectedMessage(null);
    try {
      const data = await aiApi.getMessages(channelId, folder);
      setMessages(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch messages');
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const syncMessages = useCallback(async (channelId: number, folder: string) => {
    setSyncing(true);
    setError(null);
    try {
      const result = await aiApi.sync(channelId, folder);
      setMessages(result.messages);
      return result;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Sync failed';
      setError(msg);
      throw err;
    } finally {
      setSyncing(false);
    }
  }, []);

  const fetchMessage = useCallback(async (
    channelId: number,
    folder: string,
    uid: number,
    summary?: EmailMessageSummary
  ) => {
    setError(null);
    if (summary) {
      setSelectedMessage({
        uid: summary.uid,
        mailbox: summary.mailbox,
        date: summary.date,
        from: summary.from,
        subject: summary.subject,
        isRead: true
      });
      setMessages(prev =>
        prev.map(m => (m.uid === uid && m.mailbox === folder ? { ...m, isRead: true } : m))
      );
    }
    setMessageLoading(true);
    try {
      const msg = await aiApi.getMessage(channelId, uid);
      setSelectedMessage(msg);
      setMessages(prev =>
        prev.map(m => (m.uid === uid && m.mailbox === folder ? { ...m, isRead: true } : m))
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch message');
    } finally {
      setMessageLoading(false);
    }
  }, []);

  const clearSelectedMessage = useCallback(() => {
    setSelectedMessage(null);
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
    setSelectedMessage(null);
    setError(null);
  }, []);

  const removeMessageFromList = useCallback((folder: string, uid: number) => {
    setMessages(prev => prev.filter(m => !(m.mailbox === folder && m.uid === uid)));
    setSelectedMessage(prev =>
      prev && prev.mailbox === folder && prev.uid === uid ? null : prev
    );
  }, []);

  const updateMessageReadState = useCallback((folder: string, uid: number, isRead: boolean) => {
    setMessages(prev =>
      prev.map(m => (m.uid === uid && m.mailbox === folder ? { ...m, isRead } : m))
    );
    setSelectedMessage(prev =>
      prev && prev.mailbox === folder && prev.uid === uid ? { ...prev, isRead } : prev
    );
  }, []);

  return {
    messages,
    selectedMessage,
    loading,
    syncing,
    messageLoading,
    error,
    fetchMessages,
    syncMessages,
    fetchMessage,
    clearSelectedMessage,
    clearMessages,
    removeMessageFromList,
    updateMessageReadState
  };
};
